import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { SignInRequest } from '@sourcito/schemas';
import { Button, Card, FormField, Input } from '../design';
import { useSignin } from '../lib/auth';
import { ApiCallError } from '../lib/api';

export function SigninPage() {
  const navigate = useNavigate();
  const signin = useSignin();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInRequest>({
    resolver: zodResolver(SignInRequest),
    defaultValues: { email: '', password: '' },
  });

  const errorMessage = signin.isError
    ? signin.error instanceof ApiCallError && signin.error.status === 401
      ? 'Email or password is incorrect.'
      : (signin.error as Error)?.message || 'Sign in failed. Please try again.'
    : null;

  return (
    <div className="min-h-screen bg-canvas text-fg-primary flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm space-y-8">
        <header className="space-y-2 text-center">
          <p className="text-xs uppercase tracking-wider text-fg-tertiary">Sourcito</p>
          <h1 className="text-3xl font-medium">Welcome back</h1>
          <p className="text-sm text-fg-secondary">Source smarter. Own your stack.</p>
        </header>

        <Card>
          <form
            noValidate
            onSubmit={handleSubmit(async (values) => {
              try {
                const res = await signin.mutateAsync(values);
                // 3PL warehouse staff land on their one screen — the rest of
                // the app is role-blocked server-side anyway (M2).
                navigate({ to: res.user.role === 'warehouse' ? '/app/receiving' : '/app' });
              } catch {
                // Error is surfaced via signin.isError below; keep the form mounted.
              }
            })}
            className="space-y-5"
          >
            <FormField label="Email" htmlFor="email" error={errors.email?.message} required>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={errors.email ? true : undefined}
                {...register('email')}
              />
            </FormField>

            <FormField
              label="Password"
              htmlFor="password"
              error={errors.password?.message}
              required
            >
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password ? true : undefined}
                {...register('password')}
              />
            </FormField>

            {errorMessage && (
              <div
                role="alert"
                className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2"
              >
                <p className="text-sm text-negative">{errorMessage}</p>
              </div>
            )}

            <Button type="submit" className="w-full" loading={isSubmitting || signin.isPending}>
              Sign in
            </Button>
          </form>
        </Card>

        <p className="text-xs text-fg-tertiary text-center">
          BYOK · your keys, your data, your control
        </p>
        <p className="text-[10px] text-fg-tertiary/70 text-center font-mono tabular-nums">
          v{__APP_VERSION__} · {__APP_COMMIT__}
        </p>
      </div>
    </div>
  );
}
